/**
 * SoutienLandingPage — /soutien (Sprint 4 + Polish)
 *
 * Landing générale : 2 entrées + aperçu direct (5 aides / 3 campagnes actives)
 * pour que la valeur soit immédiatement visible sans clic supplémentaire.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { HandHeart, LifeBuoy, ArrowRight, Shirt, Trophy } from "lucide-react";
import { resolveAthletePhoto } from "@/lib/photoFallback";

export default function SoutienLandingPage() {
  const [aides, setAides] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/funding?limit=5").catch(() => ({ data: { items: [] } })),
      api.get("/campaigns?limit=3").catch(() => ({ data: { items: [] } })),
    ]).then(([a, c]) => {
      setAides(a.data.items || []);
      setCampaigns(c.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8" data-testid="soutien-landing-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-display font-bold flex items-center gap-2">
          <HandHeart className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />Soutien aux athlètes
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          AITO.PF centralise les dispositifs de soutien pour les sportifs polynésiens. Deux logiques distinctes ci-dessous.
        </p>
      </div>

      {/* Deux entrées */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Link to="/aides-sportifs" data-testid="soutien-card-aides" className="block group">
          <Card className="p-5 h-full border-2 border-transparent group-hover:border-[hsl(var(--aito-lagoon))] transition">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-lg bg-[hsl(var(--aito-lagoon))]/10 flex items-center justify-center text-[hsl(var(--aito-lagoon))]"><LifeBuoy className="h-5 w-5" /></div>
              <div>
                <h2 className="font-display font-bold text-lg leading-tight">Trouver une aide</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Les dispositifs disponibles pour accompagner votre projet sportif.</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/soutien/campagnes" data-testid="soutien-card-campagnes" className="block group">
          <Card className="p-5 h-full border-2 border-transparent group-hover:border-[hsl(var(--aito-gold))] transition">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-lg bg-[hsl(var(--aito-gold))]/10 flex items-center justify-center text-[hsl(var(--aito-gold))]"><Shirt className="h-5 w-5" /></div>
              <div>
                <h2 className="font-display font-bold text-lg leading-tight">Soutenir un athlète</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Les campagnes AITO x Athlète actuellement ouvertes.</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Aperçu Aides */}
      <section className="mb-8">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Aides mises en avant</h3>
          <Link to="/aides-sportifs" className="text-sm text-[hsl(var(--aito-lagoon))] font-medium hover:underline">Voir toutes les aides <ArrowRight className="inline h-3 w-3" /></Link>
        </div>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : aides.length === 0 ? (
          <Card className="p-4 text-sm text-muted-foreground text-center">Aucune aide publiée pour le moment.</Card>
        ) : (
          <div className="grid gap-2" data-testid="soutien-aides-preview">
            {aides.slice(0, 5).map((a) => (
              <Link key={a.id} to={`/aides/${a.slug}`} className="block group">
                <Card className="p-3 hover:border-[hsl(var(--aito-lagoon))] transition">
                  <div className="flex items-start gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm leading-tight">{a.title}</div>
                      <div className="text-[11px] text-muted-foreground">{a.organizer || a.provider_name}{a.amount_text ? ` · ${a.amount_text}` : ""}</div>
                    </div>
                    {(a.categories || [])[0] && <Badge variant="outline" className="text-[10px] capitalize">{a.categories[0]}</Badge>}
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Aperçu Campagnes */}
      <section className="mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Campagnes actives</h3>
          <Link to="/soutien/campagnes" className="text-sm text-[hsl(var(--aito-gold))] font-medium hover:underline">Voir toutes les campagnes <ArrowRight className="inline h-3 w-3" /></Link>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-3 gap-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
        ) : campaigns.length === 0 ? (
          <Card className="p-4 text-sm text-muted-foreground text-center">Aucune campagne active pour le moment. Revenez bientôt.</Card>
        ) : (
          <div className="grid md:grid-cols-3 gap-3" data-testid="soutien-campaigns-preview">
            {campaigns.slice(0, 3).map((c) => {
              const t = (c.tranches || []).find((x) => x.tranche_number === c.current_tranche_number);
              const confirmed = t?.confirmed_count || 0;
              const batch = t?.batch_size || c.batch_size || 20;
              const pct = Math.min(100, Math.round((confirmed / batch) * 100));
              return (
                <Link key={c.id} to={`/soutien/campagnes/${c.slug}`} className="block group" data-testid={`soutien-campaign-${c.slug}`}>
                  <Card className="overflow-hidden hover:border-[hsl(var(--aito-gold))] transition h-full">
                    {c.athlete ? (
                      <div className="aspect-video bg-white flex items-center justify-center overflow-hidden">
                        <img src={resolveAthletePhoto(c.athlete)} alt={c.athlete.full_name}
                          className={`h-full w-full ${c.athlete.photo_url ? "object-cover object-top" : "object-contain p-3"}`} />
                      </div>
                    ) : c.image_url ? (
                      <div className="aspect-video" style={{ backgroundImage: `url(${c.image_url})`, backgroundSize: "cover", backgroundPosition: "top center" }} />
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/20 to-[hsl(var(--aito-gold))]/20 flex items-center justify-center"><Shirt className="h-8 w-8 text-muted-foreground" /></div>
                    )}
                    <div className="p-3">
                      {c.athlete && <div className="text-[10px] uppercase tracking-wide text-muted-foreground">AITO x {c.athlete.full_name}</div>}
                      <div className="font-medium text-sm leading-tight line-clamp-2">{c.title}</div>
                      <div className="mt-2 flex items-center justify-between text-[11px]"><span className="font-semibold">{confirmed}/{batch}</span>{c.price_display && <span className="text-muted-foreground">{c.price_display}</span>}</div>
                      <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-[hsl(var(--aito-lagoon))]" style={{ width: `${pct}%` }} /></div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <div className="rounded-md bg-secondary/40 p-3 text-xs border-l-4 border-[hsl(var(--aito-lagoon))]">
        <div className="font-semibold mb-0.5">Comment ça fonctionne ?</div>
        <p className="text-muted-foreground">
          AITO.PF n'encaisse aucun paiement. Le module de soutien sert à <strong>centraliser</strong> l'information sur les aides existantes et à <strong>recueillir les intentions de précommande</strong> pour les campagnes. Les démarches financières sont réalisées hors plateforme.
        </p>
      </div>
    </div>
  );
}
